import { useEffect, useState } from "react";
import { apiGet } from "../lib/api";
import { targetsMatch } from "../lib/targetKey";

/** Ignore stale pending rows (worker never started or server was overloaded). */
const PENDING_MAX_AGE_MS = 90 * 1000;
const ACTIVE_CHECK_MS = 8000;

type ActiveJob = {
  run_id: string;
  status: string;
  created_at?: string;
  started_at?: string;
  payload?: { target_company?: string };
};

function isRecentPending(job: ActiveJob): boolean {
  if (String(job.status || "").toLowerCase() !== "pending") return false;
  const created = job.created_at ? Date.parse(job.created_at) : NaN;
  if (Number.isNaN(created)) return false;
  return Date.now() - created < PENDING_MAX_AGE_MS;
}

function pickActiveJob(jobs: ActiveJob[], target: string): ActiveJob | undefined {
  const matches = jobs.filter((job) =>
    targetsMatch(String(job.payload?.target_company || ""), target),
  );
  const running = matches.find((j) => String(j.status || "").toLowerCase() === "running");
  if (running) return running;
  return matches.find(isRecentPending);
}

/** Poll for a running Phase 1 execute job matching the desk target (slow cadence). */
export function useActivePhase1RunForTarget(target: string): string {
  const [runId, setRunId] = useState("");

  useEffect(() => {
    const trimmed = target.trim();
    if (!trimmed) {
      setRunId("");
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const data = await apiGet<{ jobs: ActiveJob[] }>(
          "/api/jobs?job_type=phase1_execute&active_only=true&limit=20",
        );
        if (cancelled) return;
        const match = pickActiveJob(data.jobs || [], trimmed);
        setRunId(match?.run_id || "");
      } catch {
        if (!cancelled) setRunId("");
      }
    };

    check();
    const interval = window.setInterval(check, ACTIVE_CHECK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [target]);

  return runId;
}
